import React, { useState, useEffect } from 'react';
import { XMarkIcon, DocumentTextIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import legalService, { LegalDocument, UserLegalStatus } from '../../services/legalService';

interface TermsAcceptanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
}

interface DocumentAcceptance {
  documentId: string;
  accepted: boolean;
}

const TermsAcceptanceModal: React.FC<TermsAcceptanceModalProps> = ({
  isOpen,
  onClose,
  onAccept
}) => {
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [userStatus, setUserStatus] = useState<UserLegalStatus[]>([]);
  const [acceptances, setAcceptances] = useState<DocumentAcceptance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  console.log('TermsAcceptanceModal: isOpen =', isOpen);

  useEffect(() => {
    if (isOpen) {
      console.log('TermsAcceptanceModal: Modal aberto, carregando documentos...');
      loadLegalDocuments();
    }
  }, [isOpen]);

  const loadLegalDocuments = async () => {
    setIsLoading(true);
    try {
      const result = await legalService.checkTermsAcceptanceRequired();
      setDocuments(result.documents);
      setUserStatus(result.userStatus);
      
      // Inicializar aceites baseado no status atual
      const initialAcceptances = result.documents.map(doc => ({
        documentId: doc.id,
        accepted: result.userStatus.find(status => 
          status.documentType === doc.documentType && 
          status.audience === doc.audience
        )?.hasAcceptedLatest || false
      }));
      
      // Log para debug das URLs
      console.log('Documentos carregados:', result.documents.map(doc => ({
        id: doc.id,
        title: doc.title,
        fileUrl: doc.fileUrl,
        hasUrl: !!doc.fileUrl
      })));
      
      setAcceptances(initialAcceptances);
    } catch (error) {
      console.error('Erro ao carregar documentos legais:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptanceChange = (documentId: string, accepted: boolean) => {
    setAcceptances(prev => 
      prev.map(acc => 
        acc.documentId === documentId 
          ? { ...acc, accepted } 
          : acc
      )
    );
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Aceitar todos os documentos marcados
      const documentsToAccept = acceptances.filter(acc => acc.accepted);
      
      for (const acceptance of documentsToAccept) {
        await legalService.acceptTerms(acceptance.documentId);
      }
      
      // Limpar cache de aceite para forçar nova verificação
      legalService.clearAcceptanceCache();
      
      onAccept();
      onClose();
    } catch (error) {
      console.error('Erro ao aceitar termos:', error);
      alert('Erro ao aceitar termos. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDocumentTypeLabel = (documentType: string): string => {
    switch (documentType) {
      case 'TermsOfUse':
        return 'Termos de Uso';
      case 'PrivacyPolicy':
        return 'Política de Privacidade';
      case 'CookiePolicy':
        return 'Política de Cookies';
      case 'PartnershipTerms':
        return 'Termos de Parceria';
      default:
        return documentType;
    }
  };


  const acceptedCount = acceptances.filter(acc => acc.accepted).length;
  const totalCount = acceptances.length;
  const canSubmit = totalCount > 0 && acceptances.every(acc => acc.accepted) && !isSubmitting;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Aceite dos Termos de Uso
                </h3>
                <p className="text-gray-600">
                  Para ter acesso ao Portal do CoffeeIn Club e realizar o gerenciamento do seu estabelecimento dentro do club é necessário concluir a leitura e o aceite do termos de uso.
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Carregando documentos...</p>
            </div>
          ) : (
            <>
              {/* Documentos Legais */}
              <div className="space-y-6 mb-8">
                {documents.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Nenhum documento encontrado
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Não há documentos ativos disponíveis para aceite no momento.
                    </p>
                    <p className="text-sm text-gray-500">
                      Entre em contato com o administrador do sistema para mais informações.
                    </p>
                  </div>
                ) : (
                  documents.map((document) => {
                  const acceptance = acceptances.find(acc => acc.documentId === document.id);
                  const status = userStatus.find(s => 
                    s.documentType === document.documentType && 
                    s.audience === document.audience
                  );
                  
                  return (
                    <div key={document.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">
                            {getDocumentTypeLabel(document.documentType)}
                          </h4>
                          {document.fileUrl ? (
                            <a
                              href={document.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
                              onClick={(e) => {
                                console.log('Link clicado:', document.fileUrl);
                                e.preventDefault();
                                window.open(document.fileUrl, '_blank', 'noopener,noreferrer');
                              }}
                            >
                              <DocumentTextIcon className="h-4 w-4 mr-1" />
                              Visualizar documento
                            </a>
                          ) : (
                            <span className="text-sm text-gray-500 italic">
                              Documento não disponível
                            </span>
                          )}
                        </div>

                        {/* Checkbox de aceite */}
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`accept-${document.id}`}
                            checked={acceptance?.accepted || false}
                            onChange={(e) => handleAcceptanceChange(document.id, e.target.checked)}
                            disabled={status?.hasAcceptedLatest}
                            className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                          />
                          <label htmlFor={`accept-${document.id}`} className="ml-2 text-sm text-gray-900">
                            {status?.hasAcceptedLatest ? 'Já aceito' : 'Aceito'}
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                  })
                )}
              </div>

              {/* Informações importantes 
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Informações Importantes
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <ul className="list-disc list-inside space-y-1">
                        <li>Leia atentamente todos os documentos antes de aceitar</li>
                        <li>É necessário aceitar <strong>todos</strong> os documentos para continuar</li>
                        <li>Ao aceitar, você concorda com todos os termos e condições</li>
                        <li>Estes termos podem ser atualizados periodicamente</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              */}
              {/* Status dos aceites */}
              {totalCount > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Progresso do aceite
                    </span>
                    <span className="text-sm text-gray-600">
                      {acceptedCount} de {totalCount} documentos aceitos
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full transition-all duration-300"
                      style={{ 
                        backgroundColor: '#291911',
                        width: `${totalCount > 0 ? (acceptedCount / totalCount) * 100 : 0}%` 
                      }}
                    ></div>
                  </div>
                  {acceptedCount < totalCount && (
                    <p className="text-xs text-gray-500 mt-2">
                      Aceite todos os documentos para continuar
                    </p>
                  )}
                </div>
              )}

              {/* Botões */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="btn btn-primary flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processando...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      Aceitar Todos os Termos
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsAcceptanceModal;
